/**
 * The organization onboarding plan — one shape, one set of ordering rules.
 *
 * profile_data.onboardingStages is an authored JSON array. Three input shapes
 * reach the readers: the editor's camelCase objects, the public RPC's
 * snake_case objects (migration 20260915120000), and the legacy title-only
 * string array the RPC returned before that migration. Everything funnels
 * through normalizeOnboardingStages so the editor, the signed-in profile and
 * the anonymous profile render the same count, order and content.
 *
 * Ordering: array position and `sortOrder` are both persisted. Every writer
 * goes through withSortOrder, which rewrites sortOrder from position, so the
 * two cannot disagree in data this code writes. Readers sort by sortOrder and
 * fall back to array index for rows saved before sortOrder existed.
 */

export interface OnboardingStage {
  id: string;
  milestone: string;
  title: string;
  description: string;
  keyGoals: string[];
  keyActivities: string[];
  sortOrder: number;
}

/** The shape the editor form holds — legacy rows may lack id and sortOrder. */
export interface EditorOnboardingStage {
  id?: string;
  milestone: string;
  title: string;
  description: string;
  keyGoals?: string[];
  keyActivities?: string[];
  sortOrder?: number;
}

export const newStageId = (): string => {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `stage-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const str = (v: unknown): string => (typeof v === "string" ? v : "");

const cleanStrings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").map((x) => x.trim()).filter(Boolean) : [];

const finiteOrNull = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

interface RawStage {
  id?: unknown;
  milestone?: unknown;
  title?: unknown;
  description?: unknown;
  keyGoals?: unknown;
  goals?: unknown;
  keyActivities?: unknown;
  activities?: unknown;
  sortOrder?: unknown;
  sort_order?: unknown;
}

/**
 * One raw element (object or legacy string) to a stage with a provisional order
 * key. The display path trims text; the editor path keeps it exactly as typed,
 * because a controlled input that is re-hydrated on every keystroke would
 * otherwise drop the trailing space in "Day " before the admin types "30".
 */
const readStage = (
  raw: unknown,
  index: number,
  idFor: (raw: unknown) => string,
  text: (v: unknown) => string = (v) => str(v).trim(),
): { stage: OnboardingStage; order: number } | null => {
  if (typeof raw === "string") {
    return {
      stage: { id: idFor(raw), milestone: "", title: text(raw), description: "", keyGoals: [], keyActivities: [], sortOrder: index },
      order: index,
    };
  }
  if (!raw || typeof raw !== "object") return null;
  const r = raw as RawStage;
  const order = finiteOrNull(r.sortOrder) ?? finiteOrNull(r.sort_order) ?? index;
  return {
    stage: {
      id: typeof r.id === "string" && r.id ? r.id : idFor(raw),
      milestone: text(r.milestone),
      title: text(r.title),
      description: text(r.description),
      keyGoals: cleanStrings(r.keyGoals ?? r.goals),
      keyActivities: cleanStrings(r.keyActivities ?? r.activities),
      sortOrder: order,
    },
    order,
  };
};

const sortByOrder = (items: { stage: OnboardingStage; order: number }[]): OnboardingStage[] =>
  items
    .map((it, i) => ({ ...it, i }))
    .sort((a, b) => a.order - b.order || a.i - b.i)
    .map((it) => it.stage);

/** Rewrite sortOrder from array position. Every editor write goes through here. */
export const withSortOrder = <T extends { sortOrder?: number }>(stages: T[]): (T & { sortOrder: number })[] =>
  stages.map((s, i) => ({ ...s, sortOrder: i }));

/**
 * The display list: any input shape → ordered, titled stages only. A stage
 * without a title is not renderable and is dropped; its siblings survive.
 */
export const normalizeOnboardingStages = (input: unknown): OnboardingStage[] => {
  if (!Array.isArray(input)) return [];
  const read = input
    .map((raw, i) => readStage(raw, i, () => newStageId()))
    .filter((x): x is { stage: OnboardingStage; order: number } => x !== null && x.stage.title.length > 0);
  return withSortOrder(sortByOrder(read));
};

/**
 * The editor list: keeps untitled rows the admin is still filling in, assigns
 * an id where one is missing (through `idFor`, so a caller can keep ids stable
 * across renders for rows that have not been saved with one yet), and orders
 * by sortOrder with index fallback. Text is kept as typed — trimming here would
 * fight the controlled inputs that render it.
 */
export const hydrateEditorStages = (
  input: unknown,
  idFor: (raw: unknown) => string = () => newStageId(),
): OnboardingStage[] => {
  if (!Array.isArray(input)) return [];
  const read = input
    .map((raw, i) => readStage(raw, i, idFor, str))
    .filter((x): x is { stage: OnboardingStage; order: number } => x !== null);
  return withSortOrder(sortByOrder(read));
};

/** Move the stage at `from` to `to`. Out-of-range or no-op moves return a re-indexed copy. */
export const moveStage = <T extends { sortOrder?: number }>(stages: T[], from: number, to: number): (T & { sortOrder: number })[] => {
  const n = stages.length;
  if (from < 0 || from >= n || to < 0 || to >= n || from === to) return withSortOrder(stages);
  const next = [...stages];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return withSortOrder(next);
};

/** First integer in the milestone text ("Day 30" → 30, "90" → 90), or null. */
export const parseMilestoneDay = (milestone: string): number | null => {
  const m = /\d+/.exec(milestone ?? "");
  return m ? Number.parseInt(m[0], 10) : null;
};

/** "30" renders as "Day 30"; anything else renders as written. */
export const milestoneLabel = (milestone: string): string => {
  const t = (milestone ?? "").trim();
  return /^\d+$/.test(t) ? `Day ${Number.parseInt(t, 10)}` : t;
};

/** True when every stage with a parseable day is in non-decreasing day order. */
export const isInDayOrder = (stages: { milestone: string }[]): boolean => {
  let last = -Infinity;
  for (const s of stages) {
    const d = parseMilestoneDay(s.milestone);
    if (d === null) continue;
    if (d < last) return false;
    last = d;
  }
  return true;
};

/**
 * Move the stage at `index` to its ascending-day slot: before the first other
 * stage whose day is greater, else to the end. An unparseable stage stays put.
 */
export const placeStageByDay = <T extends { milestone: string; sortOrder?: number }>(stages: T[], index: number): (T & { sortOrder: number })[] => {
  const target = stages[index];
  const day = target ? parseMilestoneDay(target.milestone) : null;
  if (day === null) return withSortOrder(stages);
  const rest = stages.filter((_, i) => i !== index);
  let at = rest.findIndex((s) => {
    const d = parseMilestoneDay(s.milestone);
    return d !== null && d > day;
  });
  if (at === -1) at = rest.length;
  rest.splice(at, 0, target);
  return withSortOrder(rest);
};

/** Parseable stages ascending by day (stable), then unparseable stages in their existing order. */
export const sortStagesByDay = <T extends { milestone: string; sortOrder?: number }>(stages: T[]): (T & { sortOrder: number })[] => {
  const dated = stages
    .map((s, i) => ({ s, i, d: parseMilestoneDay(s.milestone) }))
    .filter((x): x is { s: T; i: number; d: number } => x.d !== null)
    .sort((a, b) => a.d - b.d || a.i - b.i)
    .map((x) => x.s);
  const undated = stages.filter((s) => parseMilestoneDay(s.milestone) === null);
  return withSortOrder([...dated, ...undated]);
};
